import { type UserType } from '@kijiji/generated/graphql-types'

import { FORM_FIELDS } from '@/components/vip/vip-trade-in/constants/vipTradeIn'
import { isNpvDealer } from '@/features/profile/utils/isNpvDealer'
import { validationFnPerField } from '@/utils/validation'

/**
 * Returns the appropriate form field validation object for the trade-in form,
 * based on the user's dealer type (NPV vs. PV).
 *
 * All dealers share a common set of required form fields (e.g., email, name, phone, year, make),
 * but some fields differ between non-passenger vehicle (NPV) and passenger vehicle (PV) dealers.
 *
 * - NPV dealers receive validation for `vehicle_type` and optional `mileage`.
 * - PV dealers receive validation for `model` and required `mileage`.
 *
 * Dealer type is determined using the `isNpvDealer` utility.
 *
 * @param userType - The user type of the dealer submitting the form.
 * @returns A mapping of form field names to their corresponding validation functions.
 */
export const getTradeInValidationObject = (userType: UserType) => {
  const validationObj = {
    [FORM_FIELDS.EMAIL]: validationFnPerField.email,
    [FORM_FIELDS.FIRST_NAME]: validationFnPerField.first_name,
    [FORM_FIELDS.LAST_NAME]: validationFnPerField.last_name,
    [FORM_FIELDS.MAKE]: validationFnPerField.text_input_required,
    [FORM_FIELDS.PHONE]: validationFnPerField.phone_number,
    [FORM_FIELDS.YEAR]: validationFnPerField.text_input_required,
  }

  const npvValidationObj = {
    [FORM_FIELDS.MILEAGE]: validationFnPerField.mileage,
    [FORM_FIELDS.VEHICLE_TYPE]: validationFnPerField.text_input_required,
  }

  const pvValidationObj = {
    [FORM_FIELDS.MILEAGE]: validationFnPerField.mileage_required,
    [FORM_FIELDS.MODEL]: validationFnPerField.text_input_required,
  }

  if (isNpvDealer(userType)) {
    return { ...validationObj, ...npvValidationObj }
  }

  return { ...validationObj, ...pvValidationObj }
}
